import { FileSpreadsheet, FileText, Image as ImageIcon, Loader2, RotateCcw, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { formatFileSize } from "@/lib/downloadFile";
import { displayFileName, extensionOf, kindByExtension } from "@/lib/executorUpload";
import type { AttachmentChip } from "@/hooks/useExecutorAttachments";

/**
 * Fila de anexos do turno, acima do campo de texto.
 *
 * ── POR QUE EXISTE UMA REGIÃO aria-live ─────────────────────────────────────
 * Este componente muda de estado SOZINHO: enviando → lendo → pronto acontece
 * sem o usuário tocar em nada. Para quem usa leitor de tela, uma transição
 * assíncrona sem região viva é invisível — a pessoa anexa um arquivo e nunca
 * fica sabendo que ele ficou pronto (nem que falhou), porque nada rouba o foco
 * e nada é anunciado. É o caso MAIS comum desta feature, não uma borda.
 *
 * A região é ÚNICA e ESTÁVEL, e só o texto dentro dela muda. Um `aria-live`
 * que monta e desmonta junto com o conteúdo frequentemente não anuncia nada:
 * o leitor precisa da região presente ANTES da mudança para observá-la.
 *
 * Visualmente os chips carregam o mesmo estado, então ninguém depende só do
 * áudio — e o texto de status nunca é só cor (falhou tem ícone, palavra e
 * ação própria).
 */

const KIND_ICON = {
  image: ImageIcon,
  pdf: FileText,
  sheet: FileSpreadsheet,
} as const;

const STATUS_COLOR: Record<AttachmentChip["status"], string> = {
  uploading: "hsl(187 72% 60%)",
  ingesting: "hsl(187 72% 60%)",
  ready: "hsl(142 60% 55%)",
  failed: "hsl(0 72% 62%)",
};

interface AttachmentChipsProps {
  chips: AttachmentChip[];
  onRemove: (localId: string) => void;
  onRetry: (localId: string) => void;
}

export default function AttachmentChips({ chips, onRemove, onRetry }: AttachmentChipsProps) {
  const { t, tf, lang } = useLanguage();

  /**
   * Copy do motivo. Todo código — do servidor ou do client — vira chave por
   * uma regra só (snake_case → camelCase sob `executor.attachment.error.`),
   * a mesma de `failureReasonI18nKey` na edge. Sem tabela paralela de textos,
   * então não há como as duas divergirem.
   */
  const reasonText = (reason: string | null): string => {
    if (!reason) return t("executor.attachment.error.internalError");
    const camel = reason.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
    return t("executor.attachment.error." + camel);
  };

  /**
   * Rótulo do chip.
   *
   * `File.name` vem do provedor de conteúdo do SO, e para foto escolhida na
   * galeria (Android content provider, biblioteca de Fotos do iOS) ele
   * costuma ser o identificador OPACO do ativo — um UUID. Foi o que apareceu
   * na primeira rodada em produção. Nada no nosso código perde o nome; o
   * sistema é que nunca deu um. Quando o nome não diz nada, mostramos o tipo
   * e a extensão, que dizem. O `original_filename` gravado no banco continua
   * sendo o original — é ele que o usuário reconhece ao baixar de volta.
   */
  const labelOf = (chip: AttachmentChip): string => {
    const original = displayFileName(chip.fileName);
    if (original) return original;
    const kind = chip.kind ?? kindByExtension(chip.fileName);
    const noun = t("executor.attachment.genericName." + (kind ?? "file"));
    const ext = extensionOf(chip.fileName);
    return ext ? noun + " ." + ext : noun;
  };

  const statusText = (chip: AttachmentChip): string => {
    if (chip.status === "uploading") return t("executor.attachment.status.uploading");
    if (chip.status === "ingesting") return t("executor.attachment.status.reading");
    if (chip.status === "ready") return t("executor.attachment.status.ready");
    return reasonText(typeof chip.reason === "string" ? chip.reason : null);
  };

  if (chips.length === 0) {
    // A região viva continua montada mesmo sem chip: é o que garante que o
    // PRIMEIRO anexo já seja anunciado.
    return <div aria-live="polite" aria-atomic="true" className="sr-only" />;
  }

  return (
    <div className="px-1 pb-2">
      {/* Anúncio único e estável — some da tela, não do leitor. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {chips
          .map((c) => tf("executor.attachment.announce", {
            file: labelOf(c),
            status: statusText(c),
          }))
          .join(". ")}
      </div>

      <ul className="flex flex-wrap gap-1.5 list-none m-0 p-0">
        {chips.map((chip) => {
          const busy = chip.status === "uploading" || chip.status === "ingesting";
          const Icon = busy ? Loader2 : chip.kind ? KIND_ICON[chip.kind] : FileText;
          const color = STATUS_COLOR[chip.status];
          return (
            <li
              key={chip.localId}
              className="flex items-center gap-2 pl-2 pr-1 py-1.5 rounded-xl max-w-[240px]"
              style={{
                background: "hsl(222 18% 12%)",
                border: "1px solid " + (chip.status === "failed"
                  ? "hsl(0 72% 62% / 0.35)"
                  : "hsl(222 18% 20%)"),
              }}
            >
              <Icon
                size={14}
                className={busy ? "animate-spin shrink-0" : "shrink-0"}
                style={{ color: color }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-display text-foreground/85 truncate leading-tight">
                  {labelOf(chip)}
                </p>
                <p className="text-[10px] font-body leading-tight" style={{ color: color }}>
                  {statusText(chip)}
                  {chip.status === "ready" && chip.byteSize > 0
                    ? " · " + formatFileSize(chip.byteSize, localeTagFor(lang))
                    : ""}
                </p>
              </div>
              {chip.status === "failed" && (
                <button
                  type="button"
                  onClick={() => onRetry(chip.localId)}
                  aria-label={tf("executor.attachment.retryFile", { file: labelOf(chip) })}
                  className="p-1 rounded-lg hover:bg-white/5 transition-colors shrink-0"
                >
                  <RotateCcw size={13} style={{ color: "hsl(38 92% 62%)" }} />
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(chip.localId)}
                aria-label={tf("executor.attachment.removeFile", { file: labelOf(chip) })}
                className="p-1 rounded-lg hover:bg-white/5 transition-colors shrink-0"
              >
                <X size={13} className="text-foreground/45" />
              </button>
            </li>
          );
        })}
      </ul>

      {/* O chip NÃO sobrevive a recarregar a conversa: loadSessionMessages lê
          só (id, role, content), então proposals — e com elas os anexos do
          turno — não são reidratadas. Resolver isso exigiria persistir as
          propostas em mirror_messages, o que está fora do escopo.
          A copy não pede desculpa: ela REDIRECIONA. Depois do PR 2 o arquivo
          está mesmo na Biblioteca, então dizer onde ele mora converte a perda
          num caminho — e só aparece quando há algo pronto, que é quando a
          frase é verdadeira. */}
      {chips.some((c) => c.status === "ready") && (
        <p className="text-[10px] font-body text-foreground/35 mt-1.5 px-0.5">
          {t("executor.attachment.savedToLibrary")}
        </p>
      )}
    </div>
  );
}
